import { safeGet } from '../../../utils/safeGet'
import { filterByWord, filterByIssuerCode, getIssuersList } from '../../../utils/filters'
import { INIT_COVERAGE_FILTERS_EDIT } from '../../../settings/scoringProfilesSettings'
import { get as _get, post as _post } from '../../../utils/fetcher'
import { criteriaToApi } from '../../../utils/efficiencyTestCriteria'

// Minimal identity a product needs for coverageCheck's excludedProducts/includedProducts entries.
// The origin must travel with it: without it the backend assumes an OKV primary-market certificate,
// so one found on the external secondary market is reported as not found. Omitted rather than sent
// as null when unknown — the backend field is a non-nullable enum with its own default.
// An empty issuer code is kept as it is: it is what the backend gives a secondary-market certificate
// whose issuer is not known, and the column it is saved to is not nullable.
function productIdentity(product) {
  return {
    // `??`-style, written out: webpack 4 cannot parse `??` in the project's own code.
    issuerCode: product.issuerCode != null ? product.issuerCode : null,
    productName: product.name || product.productName || null,
    referenceDate: product.referenceDate || null,
    isin: product.isin || null,
    ...(product.origin ? { origin: product.origin } : {}),
  }
}

// TabProfileCoverage.vue is only reachable from the catalog-driven scoring profile pages
// (EfficiencyTestsCreateNew.vue/EfficiencyTestsEdit.vue), both of which pass useNewFilters=true,
// so the coverage refresh always goes through scoringProfilesNewCommon.
function dispatchCoverageRefresh(dispatch, scoringProfileId) {
  return dispatch('scoringProfilesNewCommon/loadProfileCoverage', scoringProfileId, { root: true })
}

export default {
  namespaced: true,

  state: {
    productsList: [],
    excludedProducts: [],
    filteredIncludedProducts: [],
    filteredExcludedProducts: [],
    defaultExcludedProducts: [],
    coverageFilters: INIT_COVERAGE_FILTERS_EDIT(),
    includedProductsNumber: 0,
    excludedProductsNumber: 0,

    // /api/efficiencyTests/coverageCheck flow (TabProfileCoverage.vue) — kept separate from the
    // legacy state above, which is still fed by efficiencyTestsCommon/loadScoringProfile for
    // EfficiencyTestsDetails.vue/ProfileFeatures.vue.
    // Latest criteria snapshot handed to checkCoverage — read from ComparisonCriteria.vue via a
    // $refs call in EfficiencyTestsCreate.vue's onActivateTab, not owned by this module.
    lastCoverageCriteria: [],
    coverageManualExcluded: [],
    coverageManualIncluded: [],
    coverageCheckResult: null,
    // The last execution of the test being duplicated, read once when it is loaded: whether it failed and
    // `failures[]`. The reasons are a photograph; the peers above are recomputed (utils/failureComparison.js).
    lastExecution: { failed: false, failures: [] },
  },

  mutations: {
    setIncludedProductsNumber(state, payload) {
      state.includedProductsNumber = payload
    },

    setExcludedProductsNumber(state, payload) {
      state.excludedProductsNumber = payload
    },

    setProductsList(state, payload) {
      state.productsList = payload
    },

    setExcludedProducts(state, payload) {
      state.excludedProducts = payload
    },

    setFilteredIncludedProducts(state, payload) {
      state.filteredIncludedProducts = payload
    },

    setFilteredExcludedProducts(state, payload) {
      state.filteredExcludedProducts = payload
    },

    setDefaultExcludedProducts(state, payload) {
      state.defaultExcludedProducts = payload
    },

    setCoverageFilters(state, payload) {
      state.coverageFilters = payload
    },

    resetCoverageFilters(state) {
      state.coverageFilters = INIT_COVERAGE_FILTERS_EDIT()
    },

    setLastCoverageCriteria(state, payload) {
      state.lastCoverageCriteria = payload
    },

    setCoverageManualExcluded(state, payload) {
      state.coverageManualExcluded = payload
    },

    setCoverageManualIncluded(state, payload) {
      state.coverageManualIncluded = payload
    },

    setCoverageCheckResult(state, payload) {
      state.coverageCheckResult = payload
    },

    setLastExecution(state, payload) {
      state.lastExecution = payload
    },
  },

  getters: {
    getFilteredIncludedProducts: (state) => {
      return safeGet(() => state.filteredIncludedProducts, [])
    },

    getFilteredExcludedProducts: (state) => {
      return safeGet(() => state.filteredExcludedProducts, [])
    },

    getCoverageExcludedProducts(state) {
      return safeGet(() => state.excludedProducts, [])
    },

    getDefaultExcludedProducts(state) {
      return safeGet(() => state.defaultExcludedProducts, [])
    },

    getCoverageProductsList(state) {
      return safeGet(() => state.productsList, [])
    },

    getExcludedProducts: (state) => {
      return safeGet(() => state.excludedProducts, [])
    },

    getCoverageFilters: (state) => {
      return safeGet(() => state.coverageFilters, INIT_COVERAGE_FILTERS_EDIT())
    },

    getCoverageIssuersList(state) {
      return getIssuersList(state.productsList)
    },

    getIncludedProductsNumber(state) {
      return safeGet(() => state.includedProductsNumber, 0)
    },

    getExcludedProductsNumber(state) {
      return safeGet(() => state.excludedProductsNumber, 0)
    },

    getExcludedProductsDiff(state, getters) {
      const excludedProducts = getters.getCoverageExcludedProducts
      const defaultExcludedProducts = getters.getDefaultExcludedProducts
      if (excludedProducts.length !== defaultExcludedProducts.length) return true
      if (excludedProducts.length === 0 && defaultExcludedProducts.length === 0) return false

      const list =
        excludedProducts.length <= defaultExcludedProducts.length
          ? { source: 'getDefaultExcludedProducts', target: 'getCoverageExcludedProducts' }
          : { source: 'getCoverageExcludedProducts', target: 'getDefaultExcludedProducts' }

      const diff = getters[list.source].every((k) => {
        getters[list.target].find((j) => j.productName === k.productName)
      })
      return diff
    },

    getCoverageManualExcluded: (state) => {
      return safeGet(() => state.coverageManualExcluded, [])
    },

    getCoverageManualIncluded: (state) => {
      return safeGet(() => state.coverageManualIncluded, [])
    },

    getLastExecution: (state) => {
      return safeGet(() => state.lastExecution, { failed: false, failures: [] })
    },

    getCoverageCheckResult: (state) => {
      return safeGet(() => state.coverageCheckResult, null)
    },

    getCoverageCheckItems: (state, getters) => {
      return safeGet(() => getters.getCoverageCheckResult.items, [])
    },

    getCoverageCheckStats: (state, getters) => {
      return safeGet(() => getters.getCoverageCheckResult.coverage, {})
    },

    getCoverageCheckWarnings: (state, getters) => {
      return safeGet(() => getters.getCoverageCheckResult.warnings, [])
    },

    getCoverageCheckHasBlockingWarnings: (state, getters) => {
      return safeGet(() => getters.getCoverageCheckResult.hasBlockingWarnings, false)
    },
  },

  actions: {
    setUpProductsList({ commit, dispatch }, payload) {
      //Products received from response
      commit('setProductsList', payload.productsToSave)
      commit('setExcludedProducts', payload.excludedProducts)
      commit('setIncludedProductsNumber', payload.productsToSave.length - payload.excludedProducts.length)
      commit('setExcludedProductsNumber', payload.excludedProducts.length)
      dispatch('searchProducts')
    },

    setDefaultExcludedProducts({ commit }, value) {
      commit('setDefaultExcludedProducts', value)
    },

    setCoverageFilters({ commit }, value) {
      commit('setCoverageFilters', value)
    },

    resetCoverageFilters({ commit }) {
      commit('resetCoverageFilters')
    },

    searchProducts({ state, commit, getters }) {
      let availableProducts = state.productsList
      let filters = getters.getCoverageFilters

      if (filters.queryString && filters.queryString !== '') {
        availableProducts = filterByWord(availableProducts, filters.queryString)
      }

      if (filters.issuers.length > 0) {
        availableProducts = filterByIssuerCode(availableProducts, filters.issuers)
      }

      const includedProduct = availableProducts.filter((el) => el.isExcludedFromPerimeter === false)
      const excludedProduct = availableProducts.filter((el) => el.isExcludedFromPerimeter)

      commit('setFilteredIncludedProducts', includedProduct)
      commit('setFilteredExcludedProducts', excludedProduct)
    },

    includeCoverageProducts({ commit, dispatch, getters, rootGetters }, { products } = {}) {
      const excluded = getters.getCoverageExcludedProducts
      if (excluded.length === 0) return

      let excludedList = []
      let productsList = getters.getCoverageProductsList

      const idsToInclude = products

      productsList.map((el) => {
        if (idsToInclude.includes(el.productId)) {
          el.isExcludedFromPerimeter = false
        }
      })

      productsList.forEach((el) => {
        if (el.isExcludedFromPerimeter) {
          excludedList.push({ issuerCode: el.issuerCode, productName: el.productName })
        }
      })

      const scoringProfileId = rootGetters['efficiencyTestsCommon/getScoringProfileId']

      commit('setProductsList', productsList)
      commit('setExcludedProducts', excludedList)
      commit('setIncludedProductsNumber', productsList.length - excludedList.length)
      commit('setExcludedProductsNumber', excludedList.length)
      dispatchCoverageRefresh(dispatch, scoringProfileId)
    },

    excludeCoverageProducts({ commit, dispatch, getters, rootGetters }, { products } = {}) {
      let productsList = getters.getCoverageProductsList
      const idsToExclude = products

      productsList.map((el) => {
        if (idsToExclude.includes(el.productId)) {
          el.isExcludedFromPerimeter = true
        }
      })

      let excludedList = []

      productsList.forEach((el) => {
        if (el.isExcludedFromPerimeter) {
          excludedList.push({ issuerCode: el.issuerCode, productName: el.productName })
        }
      })

      const scoringProfileId = rootGetters['efficiencyTestsCommon/getScoringProfileId']

      commit('setProductsList', productsList)
      commit('setExcludedProducts', excludedList)
      commit('setIncludedProductsNumber', productsList.length - excludedList.length)
      commit('setExcludedProductsNumber', excludedList.length)
      dispatchCoverageRefresh(dispatch, scoringProfileId)
    },

    resetCoverageCheckState({ commit }) {
      commit('setLastCoverageCriteria', [])
      commit('setCoverageManualExcluded', [])
      commit('setCoverageManualIncluded', [])
      commit('setCoverageCheckResult', null)
      commit('setLastExecution', { failed: false, failures: [] })
    },

    excludeCoverageCheckProduct({ commit, state, dispatch }, product) {
      const identity = productIdentity(product)
      const included = state.coverageManualIncluded.filter((p) => p.isin !== identity.isin)
      const excluded = state.coverageManualExcluded.some((p) => p.isin === identity.isin)
        ? state.coverageManualExcluded
        : [...state.coverageManualExcluded, identity]

      commit('setCoverageManualIncluded', included)
      commit('setCoverageManualExcluded', excluded)
      return dispatch('checkCoverage')
    },

    // Undoes an exclusion of a proposed certificate: the certificate goes back to being a plain
    // comparable. Unlike includeCoverageCheckProduct it adds nothing to the manual inclusions.
    reincludeCoverageCheckProduct({ commit, state, dispatch }, product) {
      const identity = productIdentity(product)
      commit(
        'setCoverageManualExcluded',
        state.coverageManualExcluded.filter((p) => p.isin !== identity.isin)
      )
      return dispatch('checkCoverage')
    },

    // Really deletes a certificate added by hand — it is not excluded, it was never proposed.
    removeCoverageCheckIncludedProduct({ commit, state, dispatch }, product) {
      const identity = productIdentity(product)
      commit(
        'setCoverageManualIncluded',
        state.coverageManualIncluded.filter((p) => p.isin !== identity.isin)
      )
      return dispatch('checkCoverage')
    },

    includeCoverageCheckProduct({ commit, state, dispatch }, product) {
      const identity = productIdentity(product)
      const excluded = state.coverageManualExcluded.filter((p) => p.isin !== identity.isin)
      const included = state.coverageManualIncluded.some((p) => p.isin === identity.isin)
        ? state.coverageManualIncluded
        : [...state.coverageManualIncluded, identity]

      commit('setCoverageManualExcluded', excluded)
      commit('setCoverageManualIncluded', included)
      return dispatch('checkCoverage')
    },

    // "+ Includi per ISIN" (mock/CreazioneTest.vue) — the product isn't in `items` yet, so it's
    // resolved via GET /api/efficiencyTests/comparables/{isin} to get the full identity
    // (issuerCode/productName/referenceDate) coverageCheck expects. Response shape not confirmed:
    // a single product is assumed, an array is tolerated (first match).
    includeCoverageCheckProductByIsin({ commit, state, dispatch }, isin) {
      const normalizedIsin = (isin || '').trim().toUpperCase()
      if (!normalizedIsin || state.coverageManualIncluded.some((p) => p.isin === normalizedIsin)) {
        return Promise.resolve()
      }

      dispatch('efficiencyTestsCommon/setLoading', true, { root: true })

      return _get(`/api/efficiencyTests/comparables/${encodeURIComponent(normalizedIsin)}`)
        .then((data) => {
          const product = Array.isArray(data) ? data[0] : data
          if (!product) {
            // Same shape as a fetcher HTTP error, so callers can treat "empty" and 404 alike.
            throw { response: { status: 404 } }
          }
          const identity = productIdentity({ ...product, isin: product.isin || normalizedIsin })
          commit(
            'setCoverageManualExcluded',
            state.coverageManualExcluded.filter((p) => p.isin !== identity.isin)
          )
          commit('setCoverageManualIncluded', [...state.coverageManualIncluded, identity])
        })
        .finally(() => {
          dispatch('efficiencyTestsCommon/setLoading', false, { root: true })
        })
        .then(() => dispatch('checkCoverage'))
    },

    // `criteria` is optional — pass it (ComparisonCriteria.vue's getCriteriaPayload() output, read
    // via $refs by EfficiencyTestsCreate.vue when the Copertura tab is activated) to refresh the
    // snapshot used for the request; omit it to re-run the check with the last known snapshot (e.g.
    // after a manual include/exclude while already on this tab).
    checkCoverage({ commit, state, rootGetters, dispatch }, criteria) {
      const selectedProduct = rootGetters['efficiencyTestsCommon/getSelectedProduct']

      if (!selectedProduct) {
        return Promise.resolve()
      }

      if (criteria) {
        commit('setLastCoverageCriteria', criteria)
      }

      dispatch('efficiencyTestsCommon/setLoading', true, { root: true })

      const payload = {
        id: rootGetters['efficiencyTestsCommon/getScoringProfileId'] || 0,
        testedProductId: selectedProduct.productId,
        // `criteria` is ComparisonCriteria.vue's own getCriteriaPayload() output — that component owns
        // the criteria list/defaults, this module only relays the latest snapshot it was given.
        criteria: criteriaToApi(criteria || state.lastCoverageCriteria),
        excludedProducts: state.coverageManualExcluded,
        includedProducts: state.coverageManualIncluded,
      }

      return _post('/api/efficiencyTests/coverageCheck', payload)
        .then((data) => {
          commit('setCoverageCheckResult', data)
        })
        .finally(() => {
          dispatch('efficiencyTestsCommon/setLoading', false, { root: true })
        })
    },
  },
}
